import { useCallback, useEffect, useState } from 'react';
import {
  createReferenceMaterialFromFile,
  createTextReferenceMaterial,
  deleteReferenceMaterial,
  listReferenceMaterials,
  updateReferenceMaterial,
} from '../api/referenceMaterialApi';

export const DEFAULT_REFERENCE_LIMITS = {
  tier: 'free',
  max_total: 3,
  max_text: 3,
  file_allowed: false,
};

/**
 * 채팅 "참고 자료(내 자료함)" 상태 훅.
 *
 * - 로그인(비익명) 사용자만 로드한다. 그 외에는 항상 빈 배열.
 * - 목록/한도는 서버가 단일 진실원이다 (멀티워커·다중 탭에서도 새로고침 시 일치).
 * - 생성은 서버 응답을 그대로 반영, 수정/삭제는 낙관적 업데이트 + 실패 롤백.
 */
const useReferenceMaterials = (currentUser, isAnonymousUser, { enabled: enabledOverride } = {}) => {
  const [materials, setMaterials] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_REFERENCE_LIMITS);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const enabled = Boolean(currentUser) && !isAnonymousUser && enabledOverride !== false;
  const uid = currentUser?.uid || null;

  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await listReferenceMaterials(currentUser);
      setMaterials(Array.isArray(data.materials) ? data.materials : []);
      setLimits({ ...DEFAULT_REFERENCE_LIMITS, ...(data.limits || {}) });
      setLoadError(null);
    } catch (error) {
      setLoadError(error);
    } finally {
      setIsLoaded(true);
    }
  }, [currentUser, enabled]);

  useEffect(() => {
    if (!enabled) {
      setMaterials([]);
      setLimits(DEFAULT_REFERENCE_LIMITS);
      setIsLoaded(false);
      setLoadError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listReferenceMaterials(currentUser);
        if (cancelled) return;
        setMaterials(Array.isArray(data.materials) ? data.materials : []);
        setLimits({ ...DEFAULT_REFERENCE_LIMITS, ...(data.limits || {}) });
        setLoadError(null);
      } catch (error) {
        if (cancelled) return;
        setMaterials([]);
        setLoadError(error);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, uid]);

  const createText = useCallback(async ({ content, title }) => {
    const data = await createTextReferenceMaterial(currentUser, { content, title });
    if (data?.material) {
      setMaterials((prev) => [data.material, ...prev.filter((m) => m.id !== data.material.id)]);
    }
    return data?.material;
  }, [currentUser]);

  /**
   * 채팅 파일을 자료함에 저장한다 (칩 "자료함에 저장" · 허브 파일 올리기 공용).
   * 서버가 같은 원본 파일을 이미 저장했다고 알려주면(already_saved) 목록에 중복으로 넣지 않고
   * 기존 자료를 그대로 두되, 다른 탭/기기에서 저장돼 이 목록에 없던 자료라면 끼워 넣는다.
   * 반환값에 `already_saved` 를 실어 호출자가 "이미 있어요" 안내를 고를 수 있게 한다.
   */
  const createFromFile = useCallback(async ({ sourceFileId, title }) => {
    const data = await createReferenceMaterialFromFile(currentUser, { sourceFileId, title });
    const material = data?.material;
    if (!material) return material;
    const alreadySaved = Boolean(data.already_saved);
    setMaterials((prev) => {
      if (alreadySaved) {
        return prev.some((m) => m.id === material.id) ? prev : [...prev, material];
      }
      return [material, ...prev.filter((m) => m.id !== material.id)];
    });
    return { ...material, already_saved: alreadySaved };
  }, [currentUser]);

  const updateMaterial = useCallback(async (materialId, fields) => {
    const previous = materials;
    setMaterials((prev) => prev.map((m) => (
      m.id === materialId ? { ...m, ...fields } : m
    )));
    try {
      const data = await updateReferenceMaterial(currentUser, materialId, fields);
      if (data?.material) {
        setMaterials((prev) => prev.map((m) => (
          m.id === materialId ? data.material : m
        )));
      }
      return data?.material;
    } catch (error) {
      setMaterials(previous);
      throw error;
    }
  }, [currentUser, materials]);

  const deleteMaterial = useCallback(async (materialId) => {
    const previous = materials;
    setMaterials((prev) => prev.filter((m) => m.id !== materialId));
    try {
      await deleteReferenceMaterial(currentUser, materialId);
    } catch (error) {
      setMaterials(previous);
      throw error;
    }
  }, [currentUser, materials]);

  const markUsedLocally = useCallback((materialIds) => {
    const ids = new Set(materialIds || []);
    if (ids.size === 0) return;
    const nowIso = new Date().toISOString();
    setMaterials((prev) => prev.map((m) => (
      ids.has(m.id)
        ? { ...m, usage_count: (m.usage_count || 0) + 1, last_used_at: nowIso }
        : m
    )));
  }, []);

  return {
    materials,
    limits,
    isLoaded,
    loadError,
    enabled,
    reload,
    createText,
    createFromFile,
    updateMaterial,
    deleteMaterial,
    markUsedLocally,
  };
};

export default useReferenceMaterials;
